import React, { useEffect, useState } from 'react';
import NoIndex from '../../../components/NoIndex';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, type User } from 'firebase/auth';

// LINEログイン(OAuth)から戻ってくるページ(/service/callback)。
// LINEの code と state(=companyId) を lineApi に送り、会社の notification.lineId 等を保存する。
//
// LINEから戻るとページ全体が読み込み直されるため、表示直後は Firebase のログイン状態が
// まだ復元されておらず getAuth().currentUser は null になる。以前は表示直後に
// currentUser を見て「未ログイン」としてログイン画面へ飛ばしていたため、
// LINE連携が誰も成功しない状態になっていた。ログイン状態の復元を待ってから判定する。
const waitForAuthUser = (): Promise<User | null> =>
  new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      unsubscribe();
      resolve(user);
    });
  });

const CallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state"); // companyId として使用

    if (!code || !state) {
      console.error("code または state がありません");
      setErrorMessage("LINEからの情報が受け取れませんでした。設定画面からもう一度「LINEと連携する」を押してください。");
      return;
    }

    const sendCodeToServer = async () => {
      try {
        // サーバー側でstate(companyId)がログイン中ユーザー自身の会社かを
        // 検証できるよう、Firebase IDトークンを渡す。
        const currentUser = await waitForAuthUser();
        if (!currentUser) {
          console.error("未ログインのためLINE連携できません");
          navigate("/company/login");
          return;
        }
        const idToken = await currentUser.getIdToken();

        const response = await fetch(
          "https://us-central1-trankllc-com.cloudfunctions.net/lineApi/line/callback", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({ code, companyId: state }),
        });

        const data = await response.json().catch(() => ({}));

        if (data.success) {
          navigate("/service/setting");
        } else {
          console.error("LINE連携失敗", response.status, data);
          setErrorMessage("LINE連携に失敗しました。時間をおいて、設定画面からもう一度「LINEと連携する」を押してください。");
        }
      } catch (err) {
        console.error("LINE連携送信エラー", err);
        setErrorMessage("LINE連携に失敗しました。通信状況をご確認のうえ、設定画面からもう一度お試しください。");
      }
    };

    sendCodeToServer();
  }, [navigate]);

  return (
    <>
      <NoIndex title="LINE連携 | memorii works" />
      {errorMessage ? (
        <>
          <h4>{errorMessage}</h4>
          <button type="button" onClick={() => navigate("/service/setting")}>設定画面に戻る</button>
        </>
      ) : (
        <h4>LINE連携中...</h4>
      )}
    </>
  );
};

export default CallbackPage;
